import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * One work order's shipping address history.
 *
 * Opened from a history icon next to whatever is showing that order's address:
 * the order row in Nova Shipping's invoice Details, and the Shipping Address
 * card on the work order page. It first lived at the top of the products
 * modal, where it read as if it belonged to the products underneath it — the
 * history is a property of the ORDER's address, so it hangs off the address.
 *
 * Lives in components/ rather than under either page because both use it.
 *
 * Fetched when opened rather than carried in the group payload: only the count
 * travels with the invoice list, so an invoice with fifty orders does not ship
 * fifty logs nobody has asked to see.
 */

const addressOneLine = (a) => {
  if (!a) return "—";
  const cityLine = [a.city, a.state, a.zip].filter(Boolean).join(" ");
  return (
    [a.addressLine1, a.addressLine2, cityLine]
      .filter((l) => l && String(l).trim() !== "")
      .join(", ") || "—"
  );
};

/** What UPS said at the time of the change — three states, not two. */
const SourceNote = ({ entry }) => {
  if (entry.validated === true) {
    return <span className="text-success">UPS confirmed it</span>;
  }
  if (entry.validated === false) {
    return <span className="text-warning">UPS could not resolve it</span>;
  }
  // null: Combine writes an address without asking UPS at all, which is not
  // the same as UPS failing and should not read like it.
  return <span className="text-muted">not checked with UPS</span>;
};

export default function AddressHistoryModal({ woId, onHide }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!woId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await API.post(
        "/bundle-sync/address-history",
        { woId },
        { headers: authHeader() },
      );
      if (!res?.success) throw new Error(res?.message || "Could not load");
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setData(null);
    }
    setLoading(false);
  }, [woId]);

  useEffect(() => {
    load();
  }, [load]);

  // Newest first: the current address is the one being questioned, so the
  // change that produced it is what you want at the top.
  const entries = [...(data?.history || [])].reverse();

  return (
    <>
      <style>
        {`
          /* Its own rung on the Nova Shipping ladder — details 100000/1,
             edit 100020/1, products 100030/1, zoom 100040/1,
             combine 100050/1, Get Pdf 100060/1. Bootstrap gives every modal
             the same z-index, so one opened from another renders underneath
             it unless it claims a higher one. */
          .ns-addr-history-backdrop { z-index: 100070; }
          .ns-addr-history-modal    { z-index: 100071; }
        `}
      </style>

      <Modal
        show
        onHide={onHide}
        size="lg"
        scrollable
        centered
        backdropClassName="ns-addr-history-backdrop"
        className="ns-addr-history-modal"
        contentClassName="shadow-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5 mb-0">
            Shipping address history
            <span className="text-muted fs-6 ms-2">
              W-{woId}
              {data?.customerName ? ` · ${data.customerName}` : ""}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading && (
            <div className="text-center text-muted py-4">Loading…</div>
          )}

          {error && <div className="alert alert-danger py-2 px-3">{error}</div>}

          {!loading && !error && entries.length === 0 && (
            <div className="text-muted py-3">
              This order&apos;s shipping address has not been changed by Nova
              Shipping.
            </div>
          )}

          {!loading &&
            entries.map((h, i) => (
              <div
                key={i}
                className={`${i ? "border-top pt-3 mt-3" : ""}`}
              >
                <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                  {h.timestamp ? new Date(h.timestamp).toLocaleString() : "—"}
                  {" · "}
                  {h.by || "unknown"}
                  {" · "}
                  {h.source === "combine" ? "Combine" : "Validate"}
                  {" · "}
                  <SourceNote entry={h} />
                </div>

                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <div
                      className="text-muted text-uppercase fw-semibold mb-1"
                      style={{ fontSize: 12, letterSpacing: 0.4 }}
                    >
                      Was
                    </div>
                    <div
                      className="border rounded p-2 bg-light"
                      style={{ fontSize: 14 }}
                    >
                      {addressOneLine(h.from)}
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div
                      className="text-muted text-uppercase fw-semibold mb-1"
                      style={{ fontSize: 12, letterSpacing: 0.4 }}
                    >
                      Changed to
                    </div>
                    <div
                      className="border rounded p-2"
                      style={{ fontSize: 14 }}
                    >
                      {addressOneLine(h.to)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onHide}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
